<template>
  <b-modal
    id="modal-explainer"
    v-model="showModal"
    :hide-footer="true"
    modal-class="custom-modal2"
    @hidden="onHidden"
  >
    <template slot="modal-header" slot-scope="{ close }">
      <a href="#" class="close-button" @click="close()">×</a>
    </template>
    <template slot="default">
      <div class="explainer-inner">
        <div class="headline">
          <h2 class="title">{{ displayTitle }}</h2>
          <div class="subtitle">{{ displaySubtitle }}</div>
        </div>

        <div class="boxes">
          <div v-for="(text, i) in intros" :key="i" class="box">
            <div class="box-text">{{ text }}</div>
          </div>
        </div>

        <div class="cta clickable" @click="showModal = false">
          {{ displayButtonText }}
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { hasSeenExplainer, markExplainerSeen } from "@/utils/explainerSeen.js";

const EXPLAINER_FEATURES = {
  bonusslot: { titleKey: "bonus_slot.title", base: "bonusslot.help" },
  "bonusslot-upgrade": {
    titleKey: "bonus_slot.button.profit_info",
    base: "bonusslot.help.upgrade",
  },
  missions: { titleKey: "footer.mission_text", base: "missions.help" },
  leagues: {
    titleKey: "user_leagues.tab_button.user_league",
    base: "leagues.help",
  },
  offerchain: { base: "pay.offer.chain.help" },
  cards: { titleKey: "reward.cards", base: "cards.help" },
  friends: { titleKey: "friends.tab_button.friends", base: "friends.help" },
  seasonpass: { base: "seasonpass.help" },
  kingspath: { titleKey: "kingspath.map.quest.title", base: "kingspath.help" },
  champions: { titleKey: "champions.title", base: "champions.help" },
  tournaments: { titleKey: "tournaments.headline", base: "tournaments.help" },
};

// Global singleton: registered once in global-popups.vue. Opened from anywhere
// via `this.$eventBus.$emit("explainer:show", { feature, ... })`.
//   feature   : key into EXPLAINER_FEATURES (e.g. "leagues")
//   firstOpen : true  -> show only the first time ever on this device (then
//                        never again until the seen flag is cleared);
//               false -> show every time (default).
//   preview   : optional; use the bundled fallback texts (testo/design only).
//   params    : optional i18next interpolation values.
export default {
  name: "ModalExplainer",
  data() {
    return {
      showModal: false,
      activeFeature: null,
      titleOverride: "",
      // i18next interpolation values (e.g. { 0: invitationLevel } for friends).
      params: {},
      // Optional callback to run once the explainer is done — used by
      // "explainer-first" flows (e.g. offer chain) to open the feature after.
      continuation: null,
    };
  },
  computed: {
    config() {
      return EXPLAINER_FEATURES[this.activeFeature] || {};
    },
    displayTitle() {
      if (this.titleOverride) return this.titleOverride;
      return this.config.titleKey ? this.$t(this.config.titleKey) : "";
    },
    displaySubtitle() {
      return this.$t("help.intro.title");
    },
    displayButtonText() {
      return this.$t("events.button_lets_go");
    },
    intros() {
      return this.buildIntros(this.activeFeature, this.params);
    },
  },
  mounted() {
    this.$eventBus.$on("explainer:show", this.open);
  },
  beforeDestroy() {
    this.$eventBus.$off("explainer:show", this.open);
  },
  methods: {
    // Resolve intro1..3 for a feature via $t. A missing key renders as the key
    // string (i18next default across the app), so absent intros drop out below.
    buildIntros(feature, params) {
      const config = EXPLAINER_FEATURES[feature] || {};
      const base = config.base;
      if (!base) return [];
      return ["intro1", "intro2", "intro3"]
        .map((key) => this.$t(`${base}.${key}`, params))
        .filter((text) => text && text.trim() !== "");
    },
    // payload: "featureKey" | { feature, firstOpen, title, params, onClose }
    // onClose is a continuation: run when the explainer closes, OR immediately
    // if it won't show (already seen / no content) — so callers can chain
    // "explainer first, then open the feature" without extra branching.
    open(payload) {
      const feature =
        typeof payload === "string" ? payload : payload && payload.feature;
      const isObj = payload && typeof payload === "object";
      const proceed =
        isObj && typeof payload.onClose === "function" ? payload.onClose : null;

      if (!this.$whitelabel?.featuresEnabled?.explainers) {
        if (proceed) proceed();
        return;
      }

      if (!feature || !EXPLAINER_FEATURES[feature]) {
        if (proceed) proceed();
        return;
      }

      const firstOpen = !!(isObj && payload.firstOpen);
      const currentUser = this.$store.getters["user/currentUser"];
      const userId = currentUser && currentUser.id;
      // First-open mode: skip (but still continue) if this user already saw it.
      if (firstOpen && hasSeenExplainer(userId, feature)) {
        if (proceed) proceed();
        return;
      }

      const params = (isObj && payload.params) || {};

      if (!this.buildIntros(feature, params).length) {
        if (proceed) proceed();
        return;
      }

      this.activeFeature = feature;
      this.titleOverride = (isObj && payload.title) || "";
      this.params = params;
      this.continuation = proceed;
      this.showModal = true;

      if (firstOpen) markExplainerSeen(userId, feature);
    },
    onHidden() {
      const cont = this.continuation;
      this.continuation = null;
      this.activeFeature = null;
      this.titleOverride = "";
      if (cont) cont();
    },
  },
};
</script>

<style lang="scss">
$modal-width: 1276px;
$modal-height: 589px;

#modal-explainer {
  .modal-dialog {
    max-width: $modal-width;
    width: 100%;
    height: $modal-height;
    transform-origin: center top;
    @media (max-height: 720px), (max-width: 1330px) {
      transform: scale(0.9);
    }
    @media (max-height: 640px), (max-width: 1180px) {
      transform: scale(0.8);
    }
    @media (max-height: 570px), (max-width: 1040px) {
      transform: scale(0.7);
    }
    @media (max-width: 920px) {
      transform: scale(0.6);
    }
  }

  .modal-content {
    width: 100%;
    height: 100%;
    background: none;
    align-items: center;
  }

  .modal-body {
    flex: 0 0 auto;
    width: $modal-width;
    height: $modal-height;
    padding: 0;
    overflow: hidden;
    position: relative;
    background: url(~@/assets/img/explain/feature_introduction-background.png)
      no-repeat;
    background-size: 100% 100%;
  }

  .close-button {
    position: absolute;
    right: 18px;
    top: 12px;
    width: 44px;
    height: 44px;
    max-width: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    font-size: 34px;
    line-height: 1;
    text-decoration: none;
    z-index: 10;
  }
}
</style>

<style lang="scss" scoped>
#modal-explainer {
  .explainer-inner {
    position: absolute;
    inset: 0;
    // Keep content clear of the characters baked into the background edges.
    padding: 4% 11.5% 3.5%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .headline {
    .title {
      margin: 0;
      font-family: Ubuntu-Bold;
      font-size: 3.4rem;
      line-height: 1;
      color: #fff;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      text-shadow: 0 0 18px $text-shadow--cyan, 0 0 9px $text-shadow--cyan,
        0 0 4px rgba(0, 94, 110, 0.9);
    }

    .subtitle {
      margin-top: 0.35rem;
      font-family: Ubuntu-Medium;
      font-size: 1.7rem;
      color: #46e36a;
      text-shadow: 0 0 14px rgba(70, 227, 106, 0.75),
        0 0 6px rgba(70, 227, 106, 0.6);
    }
  }

  .boxes {
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.5%;
    // Push the boxes down a touch from the headline.
    margin-top: 10%;
  }

  .box {
    width: 31%;
    aspect-ratio: 594 / 419;
    background: url(~@/assets/img/explain/feature_introduction-box.png)
      no-repeat center / 100% 100%;
    display: flex;

    .box-text {
      padding: 8% 12% 0 13%;
      text-align: left;
      font-family: Ubuntu-Medium;
      font-size: 1.35rem;
      line-height: 1.3;
      color: #fff;
    }
  }

  .cta {
    margin-top: 0rem;
    min-width: 12rem;
    padding: 0.55rem 2.2rem;
    font-family: Ubuntu-Bold;
    font-size: 1.35rem;
    color: #fff;
    text-transform: uppercase;
    background: linear-gradient(
      to bottom,
      #4aa6d8 0%,
      #2f7fb8 55%,
      #1f5f92 100%
    );
    border: 0.09rem solid rgba(255, 255, 255, 0.85);
    border-radius: 2rem;
    box-shadow: 0 0 14px rgba(77, 229, 255, 0.55),
      inset 0 0.06rem 0.12rem rgba(255, 255, 255, 0.5);
  }
}
</style>
